import type { Tender } from '../types/tender';

export interface RelevanceUserProfile {
  tenderKeywords?: Record<string, number>;
  customPositiveKeywords?: string[];
  ignoredKeywords?: string[];
  excludedKeywords?: string[];
  negativeTenderKeywords?: Record<string, number>;
  companyCapabilityText?: string;
}

export interface RelevanceEvaluation {
  score: number;
  stars: 0 | 1 | 2 | 3;
  isRecommended: boolean;
  matchedKeywords: string[];
  disciplineMatch: boolean;
  penalizedBy: string[];
}

const STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'are',
  'as',
  'at',
  'be',
  'by',
  'for',
  'from',
  'in',
  'into',
  'is',
  'of',
  'on',
  'or',
  'that',
  'the',
  'their',
  'this',
  'to',
  'with',
  'provision',
  'service',
  'services',
  'project',
  'projects',
  'contract',
  'contracts',
  'panel',
  'panels',
  'arrangement',
  'arrangements',
  'supply',
  'delivery',
  'general',
  'other',
  'various',
  'state',
  'national',
  'australia',
  'australian',
  'nsw',
  'vic',
  'qld',
  'wa',
  'sa',
  'tas',
  'act',
  'nt',
  'department',
  'council',
  'agency',
  'authority',
  'program',
  'programme',
  'works',
  'work',
  'request',
  'tender',
  'tenders',
  'prequalification',
  'pre',
  'qualification',
  'preferred',
  'supplier',
  'suppliers',
  'not',
  'found',
  'specified',
  'defined',
  'null',
  'none',
]);

/**
 * Broad sector, context, or generic delivery terms that describe *where* a tender sits
 * or generic professional mechanics (e.g., "engineering", "consulting", "assessment", "review")
 * that appear across civil/structural/municipal tenders as well as advisory/tech tenders.
 * Matching only these terms without a specific domain/capability match should not mark a tender
 * as "Recommended" on its own, though they boost score when a specific capability matches.
 */
const CONTEXT_SECTOR_TERMS = new Set([
  'safety',
  'whs',
  'health',
  'healthcare',
  'medical',
  'rail',
  'railway',
  'transport',
  'transportation',
  'traffic',
  'government',
  'local government',
  'federal',
  'defence',
  'defense',
  'education',
  'energy',
  'utilities',
  'utility',
  'environment',
  'environmental',
  'community',
  'public',
  'regional',
  'rural',
  'indigenous',
  'veteran',
  'veterans',
  'hospital',
  'hospitals',
  'ambulance',
  'infrastructure',
  'system',
  'systems',
  'compliance',
  'management',
  'operations',
  'operational',
  'support',
  'framework',
  'standard',
  'standards',
  'policy',
  'quality',
  'risk',
  'security',
  'facility',
  'facilities',
  'asset',
  'assets',
  'network',
  'engineering',
  'consulting',
  'consultancy',
  'assessment',
  'assessments',
  'review',
  'reviews',
  'commissioning',
  'experience',
  'design',
  'writing',
  'publication',
  'history',
  'commemorative',
  'water',
  'chlorine',
  'disinfection',
  'aerodrome',
  'aviation',
  'fuel',
  'galvanised',
  'handrails',
  'tuckshop',
  'residential',
  'rehabilitation',
  'subacute',
  'palliative',
  'inpatient',
  'mechanical',
  'upgrade',
  'school',
  'yorke',
  'questacon',
  'pipeline',
  'communications',
  'engagement',
]);

/**
 * Physical construction, civil/structural/municipal works, maintenance trades,
 * equipment manufacturing/supply, and direct clinical care indicators.
 */
const PHYSICAL_OR_CLINICAL_TRADE_TERMS = [
  'fire system',
  'fire systems',
  'fire maintenance',
  'fire protection',
  'fire suppression',
  'sprinkler',
  'hydrant',
  'deluge',
  'tunnel',
  'tunnels',
  'guardrail',
  'handrail',
  'handrails',
  'galvanised',
  'galvanized',
  'tuckshop',
  'chlorine',
  'disinfection',
  'barrier',
  'barriers',
  'crash barrier',
  'wire rope',
  'roadworks',
  'road maintenance',
  'road construction',
  'road design',
  'concept design for',
  'civil works',
  'civil construction',
  'civil engineering',
  'structural engineering',
  'earthworks',
  'excavation',
  'asphalt',
  'bitumen',
  'pavement',
  'paving',
  'concrete',
  'bridge',
  'bridges',
  'culvert',
  'footpath',
  'kerb',
  'levee',
  'flood mitigation',
  'drainage',
  'sewer',
  'sewage',
  'sewerage',
  'water main',
  'water treatment',
  'wastewater',
  'stormwater',
  'geotechnical',
  'hydrographic',
  'land surveying',
  'asset condition',
  'condition assessment',
  'condition audit',
  'revaluation',
  'revaluations',
  'heritage consultancy',
  'architectural',
  'building trade',
  'building construction',
  'refurbishment',
  'demolition',
  'roofing',
  'plumbing',
  'hvac',
  'air conditioning',
  'mechanical services',
  'electrical trade',
  'switchboard',
  'switchgear',
  'circuit breaker',
  'circuit breakers',
  'welding',
  'powder coating',
  'carpentry',
  'painting',
  'repaint',
  'glazing',
  'fencing',
  'cleaning',
  'janitorial',
  'waste management',
  'landfill',
  'garbage',
  'horticulture',
  'arboriculture',
  'mowing',
  'landscaping',
  'grounds maintenance',
  'park maintenance',
  'pest control',
  'pest animal',
  'bushfire',
  'weed',
  'trailer',
  'trailers',
  'mobile van',
  'mobile vans',
  'vehicle',
  'vehicles',
  'fleet supply',
  'truck',
  'trucks',
  'bus',
  'buses',
  'heavy vehicle',
  'manufacturing',
  'fabrication',
  'machinery',
  'tractor',
  'forklift',
  'mammography',
  'breastscreen',
  'surgical',
  'surgery',
  'day procedure',
  'operating theatre',
  'paramedic',
  'ambulance service',
  'clinical staffing',
  'clinical advisory',
  'nursing',
  'dental',
  'pathology',
  'pharmacy',
  'pharmaceutical',
  'medical equipment',
  'medical consumables',
  'labware',
  'chemicals',
  'consumables',
  'microsoft licensing',
  'linen',
  'laundry',
  'catering',
  'food supply',
  'uniform',
  'uniforms',
  'ppe',
  'security guard',
  'patrol',
  'cash in transit',
  'cctv maintenance',
  'cctv installation',
  'breakdown repair',
  'traffic control signals',
];

/**
 * Curated canonical capability vocabulary used to extract high-signal domain terms from
 * unstructured Company / Partner Capability Statements and Knowledge Base summaries.
 * Synonyms, UK/US spelling variants, and morphological siblings are grouped under a single
 * canonical key so seed extraction never produces duplicate pills.
 */
const CANONICAL_CAPABILITY_VOCABULARY: Array<{ canonical: string; variants: string[] }> = [
  { canonical: 'psychology', variants: ['psychology', 'psychological'] },
  { canonical: 'engineering psychology', variants: ['engineering psychology'] },
  { canonical: 'human factors', variants: ['human factors'] },
  { canonical: 'ergonomics', variants: ['ergonomics', 'ergonomic'] },
  {
    canonical: 'behavior',
    variants: ['behavior', 'behaviour', 'behavioral', 'behavioural'],
  },
  {
    canonical: 'behavioral science',
    variants: ['behavioral science', 'behavioural science'],
  },
  { canonical: 'cognitive', variants: ['cognitive', 'cognition'] },
  { canonical: 'neuroscience', variants: ['neuroscience'] },
  { canonical: 'user research', variants: ['user research'] },
  { canonical: 'user experience', variants: ['user experience'] },
  { canonical: 'ux', variants: ['ux'] },
  { canonical: 'usability', variants: ['usability'] },
  {
    canonical: 'autonomous vehicles',
    variants: ['autonomous vehicles', 'autonomous vehicle'],
  },
  { canonical: 'risk perception', variants: ['risk perception'] },
  { canonical: 'safety culture', variants: ['safety culture'] },
  { canonical: 'human performance', variants: ['human performance'] },
  { canonical: 'workforce', variants: ['workforce'] },
  {
    canonical: 'organizational psychology',
    variants: ['organizational psychology', 'organisational psychology'],
  },
  { canonical: 'change management', variants: ['change management'] },
  { canonical: 'training', variants: ['training'] },
  { canonical: 'learning', variants: ['learning'] },
  { canonical: 'evaluation', variants: ['evaluation'] },
  { canonical: 'program evaluation', variants: ['program evaluation'] },
  { canonical: 'research', variants: ['research'] },
  { canonical: 'qualitative', variants: ['qualitative'] },
  { canonical: 'quantitative', variants: ['quantitative'] },
  { canonical: 'survey', variants: ['survey', 'surveys'] },
  { canonical: 'psychometric', variants: ['psychometric'] },
  { canonical: 'advisory', variants: ['advisory'] },
  { canonical: 'strategy', variants: ['strategy', 'strategic'] },
  { canonical: 'governance', variants: ['governance'] },
  { canonical: 'audit', variants: ['audit', 'auditing'] },
  { canonical: 'assurance', variants: ['assurance'] },
  { canonical: 'legal', variants: ['legal'] },
  { canonical: 'regulatory', variants: ['regulatory'] },
  { canonical: 'recruitment', variants: ['recruitment'] },
  { canonical: 'software', variants: ['software'] },
  { canonical: 'software development', variants: ['software development'] },
  { canonical: 'software engineering', variants: ['software engineering'] },
  { canonical: 'saas', variants: ['saas'] },
  { canonical: 'cloud', variants: ['cloud'] },
  { canonical: 'ai', variants: ['ai'] },
  { canonical: 'artificial intelligence', variants: ['artificial intelligence'] },
  { canonical: 'machine learning', variants: ['machine learning'] },
  { canonical: 'llm', variants: ['llm'] },
  { canonical: 'generative ai', variants: ['generative ai'] },
  { canonical: 'natural language', variants: ['natural language'] },
  { canonical: 'nlp', variants: ['nlp'] },
  { canonical: 'data science', variants: ['data science'] },
  { canonical: 'data analytics', variants: ['data analytics'] },
  { canonical: 'analytics', variants: ['analytics'] },
  { canonical: 'business intelligence', variants: ['business intelligence'] },
  { canonical: 'automation', variants: ['automation'] },
  { canonical: 'digital transformation', variants: ['digital transformation'] },
  { canonical: 'cybersecurity', variants: ['cybersecurity', 'cyber security'] },
  { canonical: 'information security', variants: ['information security'] },
  { canonical: 'information technology', variants: ['information technology'] },
  { canonical: 'system integration', variants: ['system integration'] },
  { canonical: 'integration', variants: ['integration'] },
  { canonical: 'devops', variants: ['devops'] },
  { canonical: 'web development', variants: ['web development'] },
  { canonical: 'mobile app', variants: ['mobile app'] },
  { canonical: 'gis', variants: ['gis'] },
  { canonical: 'spatial', variants: ['spatial'] },
  { canonical: 'economics', variants: ['economics', 'economic'] },
  { canonical: 'financial advisory', variants: ['financial advisory'] },
  { canonical: 'actuarial', variants: ['actuarial'] },
  { canonical: 'project management', variants: ['project management'] },
  { canonical: 'program management', variants: ['program management'] },
  { canonical: 'pmo', variants: ['pmo'] },
  { canonical: 'stakeholder engagement', variants: ['stakeholder engagement'] },
  { canonical: 'service design', variants: ['service design'] },
  { canonical: 'systems engineering', variants: ['systems engineering'] },
];

const UK_TO_US_MAP: Record<string, string> = {
  behaviour: 'behavior',
  behavioural: 'behavioral',
  organisation: 'organization',
  organisational: 'organizational',
  programme: 'program',
  defence: 'defense',
  analyse: 'analyze',
  analysing: 'analyzing',
  optimise: 'optimize',
  optimisation: 'optimization',
  centre: 'center',
  labour: 'labor',
  modelling: 'modeling',
  licencing: 'licensing',
};

const normCache = new Map<string, string>();
const stemCache = new Map<string, string>();

export function normalizeKeyword(raw: string): string {
  if (!raw) {
    return '';
  }
  const str = String(raw);
  if (str.length <= 80) {
    const cached = normCache.get(str);
    if (cached !== undefined) {
      return cached;
    }
  }
  const cleaned = str
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned || /^default kw \d+$/.test(cleaned)) {
    if (str.length <= 80) {
      normCache.set(str, '');
    }
    return '';
  }

  const words = cleaned.split(' ').map((w) => UK_TO_US_MAP[w] || w);
  const result = words.join(' ');
  if (str.length <= 80) {
    if (normCache.size >= 4096) {
      normCache.clear();
    }
    normCache.set(str, result);
  }
  return result;
}

export function isMeaningfulKeyword(raw: string): boolean {
  const norm = normalizeKeyword(raw);
  if (!norm || norm.length < 2) {
    return false;
  }
  if (STOP_WORDS.has(norm)) {
    return false;
  }
  const words = norm.split(' ');
  if (words.every((w) => STOP_WORDS.has(w))) {
    return false;
  }
  return true;
}

/**
 * Simple morphological stemmer for words >= 5 characters so "psychological" matches
 * "psychology", "consulting" matches "consultancy", "behavioural" matches "behaviour",
 * without ever collapsing short words like "rail" into "trailers" or "ai" into "maintenance".
 */
export function getSafeWordStem(word: string): string {
  const cached = stemCache.get(word);
  if (cached !== undefined) {
    return cached;
  }
  const w = UK_TO_US_MAP[word] || word;
  if (w.length < 5) {
    stemCache.set(word, w);
    return w;
  }
  const suffixes = [
    'ological',
    'ology',
    'ational',
    'ation',
    'ition',
    'encies',
    'ency',
    'ancies',
    'ancy',
    'ments',
    'ment',
    'nesses',
    'ness',
    'ities',
    'ity',
    'ings',
    'ing',
    'ally',
    'ical',
    'able',
    'ible',
    'ives',
    'ive',
    'al',
    'ic',
    'es',
    's',
  ];
  for (const suffix of suffixes) {
    if (w.endsWith(suffix) && w.length - suffix.length >= 4) {
      const stemmed = w.slice(0, w.length - suffix.length);
      if (stemCache.size >= 4096) {
        stemCache.clear();
      }
      stemCache.set(word, stemmed);
      return stemmed;
    }
  }
  if (stemCache.size >= 4096) {
    stemCache.clear();
  }
  stemCache.set(word, w);
  return w;
}

/**
 * Safe whole-word / phrase / morphological matcher.
 * Never performs naive `.includes()` across arbitrary character substrings.
 */
export function tokensMatch(candidateRaw: string, profileRaw: string): number {
  const candidate = normalizeKeyword(candidateRaw);
  const profile = normalizeKeyword(profileRaw);

  if (!candidate || !profile) {
    return 0;
  }

  if (candidate === profile) {
    return 1.0;
  }

  const candidateWords = candidate.split(' ');
  const profileWords = profile.split(' ');

  // Multi-word phrase contained on exact word boundaries
  if (profileWords.length > 1 && candidateWords.length >= profileWords.length) {
    const paddedCandidate = ` ${candidate} `;
    const paddedProfile = ` ${profile} `;
    if (paddedCandidate.includes(paddedProfile)) {
      return 0.95;
    }
  }
  if (candidateWords.length > 1 && profileWords.length === 1) {
    const pWord = profileWords[0];
    if (!pWord || STOP_WORDS.has(pWord)) {
      return 0;
    }
    if (candidateWords.includes(pWord)) {
      return 0.85;
    }
    if (pWord.length >= 5) {
      const pStem = getSafeWordStem(pWord);
      if (
        pStem.length >= 4 &&
        candidateWords.some((cw) => cw.length >= 5 && getSafeWordStem(cw) === pStem)
      ) {
        return 0.8;
      }
    }
    return 0;
  }

  // Single word vs single word morphological stem match (only when both words >= 5 chars)
  if (candidateWords.length === 1 && profileWords.length === 1) {
    const cWord = candidateWords[0] || '';
    const pWord = profileWords[0] || '';
    if (cWord.length >= 5 && pWord.length >= 5) {
      const cStem = getSafeWordStem(cWord);
      const pStem = getSafeWordStem(pWord);
      if (cStem.length >= 4 && cStem === pStem) {
        return 0.9;
      }
    }
  }

  return 0;
}

interface TokenizedText {
  normalized: string;
  padded: string;
  words: Set<string>;
  stems: Set<string>;
}

function tokenizeNormalizedText(normalized: string): TokenizedText {
  if (!normalized) {
    return { normalized: '', padded: '', words: new Set(), stems: new Set() };
  }
  const wordArr = normalized.split(' ');
  const words = new Set<string>(wordArr);
  const stems = new Set<string>();
  for (const w of words) {
    if (w.length >= 5) {
      const stem = getSafeWordStem(w);
      if (stem.length >= 4) {
        stems.add(stem);
      }
    }
  }
  return { normalized, padded: ` ${normalized} `, words, stems };
}

function fastTextContains(
  doc: TokenizedText,
  normTerm: string,
  termStem: string | null,
  isSingleWord: boolean
): boolean {
  if (!doc.normalized || !normTerm) {
    return false;
  }
  if (isSingleWord) {
    if (doc.words.has(normTerm)) {
      return true;
    }
    if (termStem && doc.stems.has(termStem)) {
      return true;
    }
    return false;
  }
  return doc.padded.includes(` ${normTerm} `);
}

interface CompiledTerm {
  raw: string;
  norm: string;
  isSingleWord: boolean;
  stem: string | null;
}

function compileTerm(raw: string): CompiledTerm {
  const norm = normalizeKeyword(raw);
  const isSingleWord = Boolean(norm && !norm.includes(' '));
  const stem =
    isSingleWord && norm.length >= 5
      ? (() => {
          const s = getSafeWordStem(norm);
          return s.length >= 4 ? s : null;
        })()
      : null;
  return { raw, norm, isSingleWord, stem };
}

const COMPILED_TRADE_TERMS: CompiledTerm[] = PHYSICAL_OR_CLINICAL_TRADE_TERMS.map(compileTerm);
const COMPILED_CAPABILITY_VOCABULARY: Array<{
  canonical: string;
  variants: CompiledTerm[];
}> = CANONICAL_CAPABILITY_VOCABULARY.map((entry) => ({
  canonical: entry.canonical,
  variants: entry.variants.map(compileTerm),
}));

const physicalNoiseCache = new Map<string, boolean>();
export function isPhysicalTradeKeyword(rawOrNorm: string): boolean {
  const norm = normalizeKeyword(rawOrNorm);
  if (!norm) {
    return false;
  }
  const cached = physicalNoiseCache.get(norm);
  if (cached !== undefined) {
    return cached;
  }
  const compiledCand = compileTerm(norm);
  const result = COMPILED_TRADE_TERMS.some((t) => fastTokensMatch(compiledCand, t) >= 0.8);
  physicalNoiseCache.set(norm, result);
  return result;
}

/**
 * Checks whether a normalized term or phrase appears on whole-word boundaries inside a text block.
 */

function fastTokensMatch(cand: CompiledTerm, prof: CompiledTerm): number {
  const candidate = cand.norm;
  const profile = prof.norm;
  if (!candidate || !profile) {
    return 0;
  }
  if (candidate === profile) {
    return 1.0;
  }
  if (cand.isSingleWord && prof.isSingleWord) {
    if (cand.stem && prof.stem && cand.stem === prof.stem) {
      return 0.9;
    }
    return 0;
  }
  return tokensMatchNormalized(candidate, profile);
}

function tokensMatchNormalized(candidate: string, profile: string): number {
  if (!candidate || !profile) {
    return 0;
  }
  if (candidate === profile) {
    return 1.0;
  }

  const candidateWords = candidate.split(' ');
  const profileWords = profile.split(' ');

  // Multi-word phrase contained on exact word boundaries
  if (profileWords.length > 1 && candidateWords.length >= profileWords.length) {
    const paddedCandidate = ` ${candidate} `;
    const paddedProfile = ` ${profile} `;
    if (paddedCandidate.includes(paddedProfile)) {
      return 0.95;
    }
  }
  if (candidateWords.length > 1 && profileWords.length === 1) {
    const pWord = profileWords[0];
    if (!pWord || STOP_WORDS.has(pWord)) {
      return 0;
    }
    if (candidateWords.includes(pWord)) {
      return 0.85;
    }
    if (pWord.length >= 5) {
      const pStem = getSafeWordStem(pWord);
      if (
        pStem.length >= 4 &&
        candidateWords.some((cw) => cw.length >= 5 && getSafeWordStem(cw) === pStem)
      ) {
        return 0.8;
      }
    }
    return 0;
  }

  if (candidateWords.length === 1 && profileWords.length === 1) {
    const cWord = candidateWords[0] || '';
    const pWord = profileWords[0] || '';
    if (cWord.length >= 5 && pWord.length >= 5) {
      const cStem = getSafeWordStem(cWord);
      const pStem = getSafeWordStem(pWord);
      if (cStem.length >= 4 && cStem === pStem) {
        return 0.9;
      }
    }
  }

  return 0;
}
export function textContainsWholeTerm(normalizedText: string, rawTerm: string): boolean {
  const term = normalizeKeyword(rawTerm);
  if (!term || !normalizedText) {
    return false;
  }
  const paddedText = ` ${normalizedText} `;
  if (paddedText.includes(` ${term} `)) {
    return true;
  }
  // For single words >= 5 chars, also check safe stem match against text words
  const termWords = term.split(' ');
  if (termWords.length === 1 && term.length >= 5) {
    const targetStem = getSafeWordStem(term);
    if (targetStem.length >= 4) {
      const textWords = normalizedText.split(' ');
      return textWords.some((tw) => tw.length >= 5 && getSafeWordStem(tw) === targetStem);
    }
  }
  return false;
}

const capabilityExtractionCache = new Map<string, Record<string, number>>();

/**
 * Extracts high-signal capability keywords and phrases from unstructured company capability text.
 */
export function extractCompanyProfileKeywords(capabilityText?: string): Record<string, number> {
  const trimmed = capabilityText?.trim();
  if (!trimmed) {
    return {};
  }
  const cached = capabilityExtractionCache.get(trimmed);
  if (cached) {
    return { ...cached };
  }
  const normalizedBlock = normalizeKeyword(trimmed);
  if (!normalizedBlock) {
    return {};
  }

  const doc = tokenizeNormalizedText(normalizedBlock);
  const candidates: Array<{ canonical: string; priority: number }> = [];
  for (const entry of COMPILED_CAPABILITY_VOCABULARY) {
    if (entry.variants.some((v) => fastTextContains(doc, v.norm, v.stem, v.isSingleWord))) {
      const isMultiWord = entry.canonical.includes(' ');
      const isLowSpec = LOW_SPECIFICITY_SEEDS.has(entry.canonical);
      const priority = (isMultiWord ? 3 : 2) - (isLowSpec ? 1.5 : 0);
      candidates.push({ canonical: entry.canonical, priority });
    }
  }

  // For large Knowledge Base text blocks (> 600 chars), cap to top 10 highest-specificity seeds
  const selected =
    trimmed.length > 600
      ? candidates.sort((a, b) => b.priority - a.priority).slice(0, 10)
      : candidates;

  const extracted: Record<string, number> = {};
  for (const item of selected) {
    extracted[item.canonical] = 2.5;
  }
  if (capabilityExtractionCache.size >= 32) {
    const firstKey = capabilityExtractionCache.keys().next().value;
    if (firstKey !== undefined) {
      capabilityExtractionCache.delete(firstKey);
    }
  }
  capabilityExtractionCache.set(trimmed, extracted);
  return { ...extracted };
}

export function isContextOnlyTerm(rawTerm: string): boolean {
  const norm = normalizeKeyword(rawTerm);
  return CONTEXT_SECTOR_TERMS.has(norm);
}

/**
 * Sanitizes boilerplate portal umbrella categories (like VendorPanel's
 * "Engineer, Research, Tech services") so the word "research" in the portal category
 * does not falsely trigger research matches on civil engineering tenders.
 */
function sanitizePortalCategory(category?: string): string {
  if (!category) {
    return '';
  }
  const lower = category.toLowerCase().trim();
  if (lower.includes('engineer, research, tech')) {
    return 'engineering technical services';
  }
  return category;
}

/**
 * Determines whether a tender is predominantly a physical construction, civil maintenance,
 * vehicle/equipment supply, cleaning/grounds, or direct clinical care contract.
 */
export function detectPhysicalOrClinicalTrade(tender: Tender): string[] {
  const headerText = normalizeKeyword(
    [
      tender.title,
      sanitizePortalCategory(tender.category),
      tender.projectType,
      ...(getTenderKeywordsArray(tender) || []),
    ]
      .filter(Boolean)
      .join(' ')
  );

  if (!headerText) {
    return [];
  }

  const headerDoc = tokenizeNormalizedText(headerText);
  const matchedTrades: string[] = [];
  for (const tradeTerm of COMPILED_TRADE_TERMS) {
    if (fastTextContains(headerDoc, tradeTerm.norm, tradeTerm.stem, tradeTerm.isSingleWord)) {
      matchedTrades.push(tradeTerm.raw);
    }
  }
  return matchedTrades;
}

export function getTenderKeywordsArray(tender: Tender): string[] {
  if (!tender.keywords) {
    return [];
  }
  const raw = Array.isArray(tender.keywords)
    ? tender.keywords
    : String(tender.keywords)
        .split(',')
        .map((k) => k.trim());
  return raw.filter((kw) => isMeaningfulKeyword(kw));
}

export const MIN_RECURRING_HISTORICAL_COUNT = 2;
export const MAX_HISTORICAL_KEYWORDS = 8;
export const MAX_AUTO_ACTIVE_KEYWORDS = 8;
export const MAX_NEUTRAL_KEYWORDS = 8;

const GENERIC_DELIVERY_QUALIFIERS = new Set([
  'advisory',
  'consulting',
  'consultancy',
  'design',
  'management',
  'maintenance',
  'support',
  'system',
  'systems',
  'assessment',
  'audit',
  'supply',
  'installation',
  'control',
  'equipment',
  'consumables',
  'services',
  'service',
]);

const LOW_SPECIFICITY_SEEDS = new Set([
  'learning',
  'training',
  'strategy',
  'governance',
  'audit',
  'assurance',
  'legal',
  'regulatory',
  'recruitment',
  'software',
  'software development',
  'software engineering',
  'cloud',
  'integration',
  'system integration',
  'analytics',
  'automation',
  'project management',
  'program management',
  'pmo',
  'stakeholder engagement',
]);

export interface EffectiveKeywordWeights {
  positiveWeights: Map<
    string,
    {
      display: string;
      weight: number;
      isContextOnly: boolean;
      isSingleWord: boolean;
      stem: string | null;
    }
  >;
  ignoredSet: Set<string>;
  excludedSet: Set<string>;
  excludedCompiled: CompiledTerm[];
  negativeWeights: Map<string, number>;
  negativeCompiled: Array<CompiledTerm & { count: number }>;
  userTargetsPhysicalPool: string[];
}

const compiledProfileWeakCache = new WeakMap<RelevanceUserProfile, EffectiveKeywordWeights>();

/**
 * Builds the unified positive and negative keyword weights from the user's profile,
 * company capability text, custom keywords, and archive feedback.
 */
export function buildEffectiveKeywordWeights(
  profile: RelevanceUserProfile
): EffectiveKeywordWeights {
  const cached = compiledProfileWeakCache.get(profile);
  if (cached) {
    return cached;
  }

  const excludedSet = new Set<string>();
  const excludedCompiled: CompiledTerm[] = [];
  for (const ex of profile.excludedKeywords || []) {
    const norm = normalizeKeyword(ex);
    if (norm && !excludedSet.has(norm)) {
      excludedSet.add(norm);
      excludedCompiled.push(compileTerm(norm));
    }
  }

  const ignoredSet = new Set<string>();
  for (const ig of profile.ignoredKeywords || []) {
    const norm = normalizeKeyword(ig);
    if (norm && !excludedSet.has(norm)) {
      ignoredSet.add(norm);
    }
  }

  const negativeWeights = new Map<string, number>();
  const negativeCompiled: Array<CompiledTerm & { count: number }> = [];
  if (profile.negativeTenderKeywords) {
    for (const [kw, count] of Object.entries(profile.negativeTenderKeywords)) {
      const norm = normalizeKeyword(kw);
      if (norm && count > 0 && !CONTEXT_SECTOR_TERMS.has(norm) && !negativeWeights.has(norm)) {
        negativeWeights.set(norm, count);
        negativeCompiled.push({ ...compileTerm(norm), count });
      }
    }
  }

  const positiveWeights = new Map<
    string,
    {
      display: string;
      weight: number;
      isContextOnly: boolean;
      isSingleWord: boolean;
      stem: string | null;
    }
  >();

  const upsertPositive = (
    rawKw: string,
    weightToAdd: number,
    replaceIfHigher = false,
    forceDiscipline = false
  ) => {
    if (!isMeaningfulKeyword(rawKw)) {
      return;
    }
    const norm = normalizeKeyword(rawKw);
    if (!norm || excludedSet.has(norm) || ignoredSet.has(norm)) {
      return;
    }
    const display = String(rawKw).replace(/_/g, ' ').trim().toLowerCase();
    const isContextOnly = forceDiscipline ? false : isContextOnlyTerm(norm);
    const existing = positiveWeights.get(norm);
    if (existing) {
      existing.weight = replaceIfHigher
        ? Math.max(existing.weight, weightToAdd)
        : existing.weight + weightToAdd;
      if (forceDiscipline) {
        existing.isContextOnly = false;
      }
    } else {
      const compiled = compileTerm(norm);
      positiveWeights.set(norm, {
        display,
        weight: weightToAdd,
        isContextOnly,
        isSingleWord: compiled.isSingleWord,
        stem: compiled.stem,
      });
    }
  };

  // 1. Company capability profile seeds
  const companySeeds = extractCompanyProfileKeywords(profile.companyCapabilityText);
  const companySeedKeys = Object.keys(companySeeds);
  for (const [kw, seedWeight] of Object.entries(companySeeds)) {
    upsertPositive(kw, seedWeight, true, true);
  }

  // 2. Historical tender keywords (adaptive cold-start: require count >= 2 when user has seeds/custom/recurring signals; allow count >= 1 on cold start)
  if (profile.tenderKeywords) {
    const entries = Object.entries(profile.tenderKeywords).filter(
      ([kw, rawCount]) =>
        typeof rawCount === 'number' &&
        rawCount > 0 &&
        isMeaningfulKeyword(kw) &&
        !excludedSet.has(normalizeKeyword(kw)) &&
        !ignoredSet.has(normalizeKeyword(kw))
    );
    const hasRecurringSignal = entries.some(
      ([kw, c]) => c >= MIN_RECURRING_HISTORICAL_COUNT && !isContextOnlyTerm(kw)
    );
    const hasEstablishedProfile =
      companySeedKeys.length > 0 ||
      (profile.customPositiveKeywords && profile.customPositiveKeywords.length > 0) ||
      hasRecurringSignal;
    const minCount = hasEstablishedProfile ? MIN_RECURRING_HISTORICAL_COUNT : 1;

    const validHistoricalEntries = entries
      .filter(([kw, rawCount]) => {
        if (rawCount < minCount) {
          return false;
        }
        const norm = normalizeKeyword(kw);
        if (!companySeeds[norm] && hasEstablishedProfile && isPhysicalTradeKeyword(norm)) {
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        const aContext = isContextOnlyTerm(a[0]);
        const bContext = isContextOnlyTerm(b[0]);
        if (aContext !== bContext) {
          return aContext ? 1 : -1;
        }
        return b[1] - a[1];
      })
      .slice(0, MAX_HISTORICAL_KEYWORDS + 10);

    for (const [kw, rawCount] of validHistoricalEntries) {
      const norm = normalizeKeyword(kw);
      const matchingSeed = companySeedKeys.find(
        (seed) => seed === norm || tokensMatchNormalized(norm, seed) >= 0.9
      );
      const dampenedWeight = Math.min(3.5, 1 + Math.log2(rawCount + 1) * 0.9);
      if (matchingSeed) {
        // Do not double-stack weight when already present as a company seed; take the stronger signal
        upsertPositive(matchingSeed, dampenedWeight, true);
      } else {
        upsertPositive(norm, dampenedWeight, true);
      }
    }
  }

  // 3. Explicit user custom positive keywords (highest priority, always treated as core discipline)
  for (const customKw of profile.customPositiveKeywords || []) {
    upsertPositive(customKw, 3.5, true, true);
  }

  const userTargetsPhysicalPool: string[] = [];
  for (const [pNorm, entry] of positiveWeights.entries()) {
    if (!entry.isContextOnly) {
      userTargetsPhysicalPool.push(pNorm);
    }
  }

  const compiled: EffectiveKeywordWeights = {
    positiveWeights,
    ignoredSet,
    excludedSet,
    excludedCompiled,
    negativeWeights,
    negativeCompiled,
    userTargetsPhysicalPool,
  };
  compiledProfileWeakCache.set(profile, compiled);
  return compiled;
}

interface CompiledTender {
  tenderKeywords: string[];
  compiledKeywords: CompiledTerm[];
  titleDoc: TokenizedText;
  categoryDoc: TokenizedText;
  scopeDoc: TokenizedText;
  detectedTrades: CompiledTerm[];
}

const compiledTenderWeakCache = new WeakMap<Tender, CompiledTender>();

function getCompiledTender(tender: Tender): CompiledTender {
  const cached = compiledTenderWeakCache.get(tender);
  if (cached) {
    return cached;
  }
  const tenderKeywords = getTenderKeywordsArray(tender);
  const compiledKeywords = tenderKeywords.map(compileTerm);
  const sanitizedCat = sanitizePortalCategory(tender.category);
  const titleNorm = normalizeKeyword(tender.title || '');
  const categoryNorm = normalizeKeyword(
    [sanitizedCat, tender.projectType, tender.sector].filter(Boolean).join(' ')
  );
  const scopeNorm = normalizeKeyword(
    [tender.scope, tender.summary, tender.description].filter(Boolean).join(' ').slice(0, 1500)
  );
  const titleDoc = tokenizeNormalizedText(titleNorm);
  const categoryDoc = tokenizeNormalizedText(categoryNorm);
  const scopeDoc = tokenizeNormalizedText(scopeNorm);

  const headerNorm = [titleNorm, categoryNorm, ...compiledKeywords.map((k) => k.norm)]
    .filter(Boolean)
    .join(' ');
  const headerDoc = tokenizeNormalizedText(headerNorm);
  const detectedTrades: CompiledTerm[] = [];
  if (headerNorm) {
    for (const tradeTerm of COMPILED_TRADE_TERMS) {
      if (fastTextContains(headerDoc, tradeTerm.norm, tradeTerm.stem, tradeTerm.isSingleWord)) {
        detectedTrades.push(tradeTerm);
      }
    }
  }

  const compiled: CompiledTender = {
    tenderKeywords,
    compiledKeywords,
    titleDoc,
    categoryDoc,
    scopeDoc,
    detectedTrades,
  };
  compiledTenderWeakCache.set(tender, compiled);
  return compiled;
}

/**
 * Evaluates a public tender against the user's relevance profile with:
 * - Whole-token & safe morphological stem matching (zero substring collisions)
 * - Service Discipline vs. Industry Sector gating
 * - Physical/Clinical trade mismatch gating
 * - Negative archive & muted keyword penalties
 */
export function evaluateTenderRelevance(
  tender: Tender,
  profile: RelevanceUserProfile
): RelevanceEvaluation {
  const {
    positiveWeights,
    excludedSet,
    excludedCompiled,
    negativeCompiled,
    userTargetsPhysicalPool,
  } = buildEffectiveKeywordWeights(profile);

  if (positiveWeights.size === 0) {
    return {
      score: 0,
      stars: 0,
      isRecommended: false,
      matchedKeywords: [],
      disciplineMatch: false,
      penalizedBy: [],
    };
  }

  const { compiledKeywords, titleDoc, categoryDoc, scopeDoc, detectedTrades } =
    getCompiledTender(tender);

  // Check explicit exclusions first
  const penalizedBy: string[] = [];
  for (const ex of excludedCompiled) {
    const matchesKeyword = compiledKeywords.some((tk) => fastTokensMatch(tk, ex) >= 0.8);
    const matchesHeader =
      fastTextContains(titleDoc, ex.norm, ex.stem, ex.isSingleWord) ||
      fastTextContains(categoryDoc, ex.norm, ex.stem, ex.isSingleWord);
    if (matchesKeyword || matchesHeader) {
      penalizedBy.push(ex.norm);
    }
  }

  // Check negative archive feedback keywords
  let archivePenalty = 0;
  for (const neg of negativeCompiled) {
    const matchesKeyword = compiledKeywords.some((tk) => fastTokensMatch(tk, neg) >= 0.85);
    const matchesTitle = fastTextContains(titleDoc, neg.norm, neg.stem, neg.isSingleWord);
    if (matchesKeyword || matchesTitle) {
      if (!positiveWeights.has(neg.norm)) {
        archivePenalty += Math.min(4.0, neg.count * 1.5);
        penalizedBy.push(neg.norm);
      }
    }
  }

  let disciplineScore = 0;
  let contextScore = 0;
  const matchedList: Array<{ display: string; points: number; isDiscipline: boolean }> = [];

  for (const [profileNorm, entry] of positiveWeights.entries()) {
    let bestMultiplier = 0;
    const profCompiled: CompiledTerm = {
      raw: profileNorm,
      norm: profileNorm,
      isSingleWord: entry.isSingleWord,
      stem: entry.stem,
    };

    // 1. Check tender.keywords
    for (const tKw of compiledKeywords) {
      const m = fastTokensMatch(tKw, profCompiled);
      if (m > bestMultiplier) {
        bestMultiplier = m;
      }
    }

    // 2. Check tender title & category/projectType (strong signal)
    if (fastTextContains(titleDoc, profileNorm, entry.stem, entry.isSingleWord)) {
      bestMultiplier = Math.max(bestMultiplier, 1.1);
    } else if (fastTextContains(categoryDoc, profileNorm, entry.stem, entry.isSingleWord)) {
      bestMultiplier = Math.max(bestMultiplier, 0.95);
    } else if (
      bestMultiplier === 0 &&
      !entry.isContextOnly &&
      fastTextContains(scopeDoc, profileNorm, entry.stem, entry.isSingleWord)
    ) {
      // Core capability mentioned in scope/summary even if omitted from the 5 keywords
      bestMultiplier = 0.65;
    }

    if (bestMultiplier > 0) {
      const points = entry.weight * bestMultiplier;
      if (entry.isContextOnly) {
        contextScore += points;
        matchedList.push({ display: entry.display, points, isDiscipline: false });
      } else {
        disciplineScore += points;
        matchedList.push({ display: entry.display, points, isDiscipline: true });
      }
    }
  }

  // Check if the tender is a physical/civil/clinical trade that conflicts with a non-physical profile
  const userTargetsPhysicalTrade =
    detectedTrades.length > 0 &&
    detectedTrades.some((trade) =>
      userTargetsPhysicalPool.some((pNorm) => {
        if (pNorm === trade.norm) {
          return true;
        }
        if (GENERIC_DELIVERY_QUALIFIERS.has(pNorm)) {
          return false;
        }
        return tokensMatchNormalized(trade.norm, pNorm) >= 0.85;
      })
    );

  const hasTradeMismatch = detectedTrades.length > 0 && !userTargetsPhysicalTrade;

  // Explicitly muted keyword on card/header zeroes out recommendation immediately
  if (penalizedBy.some((p) => excludedSet.has(p))) {
    return {
      score: 0,
      stars: 0,
      isRecommended: false,
      matchedKeywords: [],
      disciplineMatch: false,
      penalizedBy,
    };
  }

  const disciplineMatch = disciplineScore > 0 && !hasTradeMismatch;

  let finalScore = 0;
  if (hasTradeMismatch) {
    // Physical/clinical trade tender (e.g. Fire Systems Maintenance, Guardrail Supply, BreastScreen Trailers, Civil Levee Design)
    if (disciplineScore >= 6.0) {
      finalScore = Math.max(0, disciplineScore - 4.5);
    } else {
      finalScore = 0;
      penalizedBy.push(...detectedTrades.slice(0, 2).map((t) => t.raw));
    }
  } else if (disciplineScore > 0) {
    // Genuine capability/discipline match! Context/sector matches act as a synergy boost
    const cappedContextBoost = Math.min(contextScore * 0.65, disciplineScore * 1.2 + 2.0);
    finalScore = disciplineScore + cappedContextBoost - archivePenalty;
  } else {
    // Context-only match (e.g. only matched "safety", "health", "rail", or generic "engineering"/"consulting")
    // Never mark as Recommended; keep below 1.0 so it doesn't get 2-3 stars or pollute Recommended
    finalScore = Math.max(0, Math.min(0.8, contextScore * 0.15) - archivePenalty);
  }

  finalScore = Math.max(0, Number(finalScore.toFixed(2)));

  // Sort matched keywords with discipline matches first, then by points descending
  matchedList.sort((a, b) => {
    if (a.isDiscipline !== b.isDiscipline) {
      return a.isDiscipline ? -1 : 1;
    }
    return b.points - a.points;
  });

  const matchedKeywords = Array.from(new Set(matchedList.map((m) => m.display)));

  let stars: 0 | 1 | 2 | 3 = 0;
  if (disciplineMatch && finalScore >= 5.5) {
    stars = 3;
  } else if (disciplineMatch && finalScore >= 3.0) {
    stars = 2;
  } else if (disciplineMatch && finalScore >= 1.5) {
    stars = 1;
  } else {
    stars = 0;
  }

  const isRecommended = disciplineMatch && finalScore >= 1.5;

  return {
    score: finalScore,
    stars,
    isRecommended,
    matchedKeywords: finalScore > 0 ? matchedKeywords : [],
    disciplineMatch,
    penalizedBy,
  };
}
